// This file contains the custom client for wishlisting products in Shopify. Wishlist is done using Swym

declare global {
  interface Window {
    _swat?: {
      initializeActionButtons?: (selector: string) => void
      currentPageData?: {
        du?: string
      }
    }
    Shopify?: {
      routes: {
        root: string
      }
    }
  }
}

/**
 * Adds a product variant to the wishlist using Swym API
 * @param variantId - The Shopify variant ID (as a string)
 * @param productUrl - Product URL (required)
 * @param productId - Product ID (required)
 * @throws Error if Swym API is not available or if the operation fails
 */
export const addVariantToWishlist = async (
  variantId: string,
  productUrl?: string,
  productId?: number
): Promise<void> => {
  if (!window._swat) {
    throw new Error('Swym wishlist API is not loaded')
  }

  if (!variantId || variantId.trim().length === 0) {
    throw new Error(`Invalid variant ID: ${variantId}`)
  }

  if (!productId) {
    throw new Error('Product ID is required for adding to wishlist')
  }

  // Convert myshopify.com URL to public domain if needed
  let publicProductUrl = productUrl
  if (productUrl && productUrl.includes('.myshopify.com')) {
    const currentPageUrl = window._swat.currentPageData?.du
    if (currentPageUrl) {
      const publicDomain = new URL(currentPageUrl).hostname
      const productPath = productUrl.split('/products/')[1]
      if (productPath) {
        publicProductUrl = `https://${publicDomain}/products/${productPath}`
      }
    } else if (window.Shopify?.routes?.root) {
      const root = window.Shopify.routes.root
      const productPath = productUrl.split('/products/')[1]
      if (productPath) {
        publicProductUrl = `${root}products/${productPath}`
      }
    }
  }

  if (!publicProductUrl) {
    throw new Error('Product URL is required for adding to wishlist')
  }

  // Create a temporary button with Swym data attributes
  const button = document.createElement('button')
  button.style.position = 'fixed'
  button.style.left = '-9999px'
  button.style.top = '-9999px'
  button.style.opacity = '0'
  button.style.pointerEvents = 'none'
  button.setAttribute('data-with-epi', 'true')
  button.setAttribute('data-swaction', 'addToWishlist')
  button.setAttribute('data-product-id', productId.toString())
  button.setAttribute('data-variant-id', variantId)
  button.setAttribute('data-product-url', publicProductUrl)
  button.setAttribute('class', 'swym-button swym-add-to-wishlist')
  button.setAttribute('id', `swym-wishlist-btn-${Date.now()}`)

  document.body.appendChild(button)

  try {
    if (window._swat.initializeActionButtons) {
      window._swat.initializeActionButtons('body')
      await new Promise(resolve => setTimeout(resolve, 200))
      window._swat.initializeActionButtons(`#${button.id}`)
      await new Promise(resolve => setTimeout(resolve, 200))
    }

    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    })
    button.dispatchEvent(clickEvent)
    button.click()

    await new Promise(resolve => setTimeout(resolve, 1000))
  } catch (error) {
    throw new Error(`Failed to add product to wishlist: ${error instanceof Error ? error.message : 'Unknown error'}`)
  } finally {
    setTimeout(() => {
      if (button.parentNode) {
        button.parentNode.removeChild(button)
      }
    }, 2000)
  }
}
